// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import { liftToAttemptFieldName, liftToStatusFieldName } from "../../logic/entry";
import { displayWeight, kg2lbs, lbs2kg, string2number } from "../../logic/units";
import { Entry, Language, Lift, Validation } from "../../types/dataTypes";

import styles from "./OperationLayer.module.scss";

interface Props {
  entry: Entry;
  lift: Lift;
  attemptOneIndexed: number;
  inKg: boolean;
  language: Language;
  onEnterAttempt: (entryId: number, lift: Lift, attemptOneIndexed: number, weightKg: number) => void;
}

interface InternalState {
  lastGoodValue: string;
  value: string;
}

class OperationAttemptInput extends React.Component<Props, InternalState> {
  constructor(props: Props) {
    super(props);

    const fieldKg = liftToAttemptFieldName(props.lift);
    const weightKg = props.entry[fieldKg][props.attemptOneIndexed - 1];
    const weightStr = weightKg === 0 ? "" : displayWeight(props.inKg ? weightKg : kg2lbs(weightKg), props.language);

    this.state = {
      lastGoodValue: weightStr,
      value: weightStr,
    };
  }

  validate = (): Validation => {
    let value = this.state.value;
    if (value === "") return null;

    value = value.replace(",", ".");
    const asNumber = Number(value);
    if (isNaN(asNumber)) return "error";
    if (!isFinite(asNumber)) return "error";
    if (asNumber < 0) return "error";

    if (this.props.attemptOneIndexed > 1) {
      const asKg = this.props.inKg ? asNumber : lbs2kg(asNumber);
      const fieldKg = liftToAttemptFieldName(this.props.lift);
      const fieldStatus = liftToStatusFieldName(this.props.lift);

      for (let i = 0; i < this.props.attemptOneIndexed - 1; ++i) {
        const prevWeightKg = this.props.entry[fieldKg][i];
        if (prevWeightKg > asKg) return "error";
        if (prevWeightKg === asKg) {
          const prevStatus = this.props.entry[fieldStatus][i];
          if (prevStatus === 1) return "error";
          if (prevStatus === 0) return "warning";
        }
      }
    }

    if (asNumber % 2.5 !== 0) return "warning";
    return null;
  };

  handleChange = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (typeof value === "string") {
      this.setState({ value: value.replace(" ", "").replace("e", ".") });
    }
  };

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  handleBlur = () => {
    if (this.validate() === "error") {
      this.setState({ value: this.state.lastGoodValue });
      return;
    }

    const asNumber = string2number(this.state.value);
    const weightKg = this.props.inKg ? asNumber : lbs2kg(asNumber);
    this.props.onEnterAttempt(this.props.entry.id, this.props.lift, this.props.attemptOneIndexed, weightKg);
    this.setState({ lastGoodValue: this.state.value });
  };

  render() {
    return (
      <input
        aria-label={`${this.props.entry.name} ${this.props.lift}${this.props.attemptOneIndexed}`}
        className={styles.attemptInput}
        value={this.state.value}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
      />
    );
  }
}

export default OperationAttemptInput;
